import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import { server } from "../server";
import axios from "axios";

// Converts a raw mark into a number, or null if the student was absent.
// Handles "AB", "ab", "A", "", null, undefined, 0 and anything non-numeric.
// Valid marks are always rounded UP (e.g. 17.25 -> 18).
const parseMark = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim().toUpperCase();
  if (str === "" || str === "AB" || str === "A" || str === "ABSENT")
    return null;
  const num = Number(str);
  if (Number.isNaN(num) || num === 0) return null;
  return Math.ceil(num);
};

const DownloadResult = async ({
  marks,
  heads,
  attendanceData,
  selectedClass,
  selectedSection,
  workingDays,
}) => {
  marks.sort((a, b) => {
    const rollA = Number(a.rollNo) || Infinity;
    const rollB = Number(b.rollNo) || Infinity;
    return rollA - rollB;
  });

  const subjectOrder = [
    "English",
    "Hindi",
    "Maths",
    "EVS",
    "Science",
    "SST",
    "Sanskrit",
    "GK",
    "AI",
  ];

  const halfYearlyConfig = heads[0].examHeads.find(
    (e) => e.examType.toLowerCase() === "halfyearly",
  );

  const headLabels = halfYearlyConfig.heads;

  const getGrade = (percentage) => {
    if (percentage >= 91) return "A1";
    if (percentage >= 81) return "A2";
    if (percentage >= 71) return "B1";
    if (percentage >= 61) return "B2";
    if (percentage >= 51) return "C1";
    if (percentage >= 41) return "C2";
    if (percentage >= 33) return "D";
    return "E";
  };

  const halfYearlyRemarks = halfYearlyConfig.remarks;

  // -------- CLASS TEACHER (fetched once for this class & section) ----------
  let classTeacherStaffId = "";
  try {
    const teacherRes = await axios.post(
      `${server}/GetUserDetailByClassTeacherAndSection`,
      { classTeacher: selectedClass, section: selectedSection },
      {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      },
    );
    classTeacherStaffId = teacherRes?.data?.data?.staffId || "";
  } catch (err) {
    console.error("Failed to fetch class teacher", err);
  }

  const classTeacherSignHtml = classTeacherStaffId
    ? `<img src="/assets/images/teachers/${classTeacherStaffId}.png" width="140" crossOrigin="anonymous" class="mb-n3" />`
    : "";

  const pdf = new jsPDF("l", "mm", "a4");

  for (let i = 0; i < marks.length; i++) {
    let student = marks[i]; // start with the current mark object

    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: student.admission_no },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const studentData = response.data?.["body-json"]?.body?.student;
      if (studentData) {
        student = { ...student, ...studentData };
      }
    } catch (error) {
      console.error(`Failed to fetch data for ${student.admission_no}:`, error);
    }

    const studentName = student?.name || "-";
    const fatherName = student?.parentDetails?.[0]?.name || "-";
    const motherName = student?.parentDetails?.[1]?.name || "-";
    const currentClass = student?.currentClass || "-";
    const section = student?.studentDetails?.currentSection || "-";
    const studentImage = student?.studentDetails?.studentImage || "-";
    const rollNo = student?.studentDetails?.rollNo || "-";
    const dobRaw = student?.dateOfBirth || "-";
    const dob = dobRaw !== "-" ? dobRaw.split("-").reverse().join("/") : "-";

    const studentAttendance =
      attendanceData.find((att) => att.admission_no === student.admission_no)
        ?.attendance || [];

    const totalPresentDays = studentAttendance.reduce((count, day) => {
      return count + (day.SchoolStatus?.toLowerCase() === "present" ? 1 : 0);
    }, 0);

    const attendancePercentage =
      workingDays > 0
        ? ((totalPresentDays / workingDays) * 100).toFixed(2)
        : "0.00";

    // -------- SUBJECTS ----------
    const normalize = (str) => str?.trim().toLowerCase();

    const studentSubjects = student.result?.["half yearly"] || [];
    const subjectMap = {};
    studentSubjects.forEach((sub) => {
      subjectMap[normalize(sub.subject)] = sub;
    });

    const cell = (content) =>
      `<td class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${content}</p></td>`;

    let subjectRowsHtml = "";
    let studentObtainedTotal = 0;
    let studentMaxTotal = 0;

    subjectOrder.forEach((subjectName) => {
      const subjectObj = subjectMap[normalize(subjectName)];
      if (!subjectObj) return;

      const marksByHead = subjectObj.marks || {};
      let totalObtained = 0;
      let totalMax = 0;

      const headCells = headLabels
        .map((h) => {
          if (h.label.toLowerCase() === "weekly test 1") {
            const s1 = parseMark(marksByHead["h1"]);
            const s2 = parseMark(marksByHead["h2"]);

            totalMax += 10;
            studentMaxTotal += 10;

            // Absent in both weekly tests -> AB
            if (s1 === null && s2 === null) return cell("AB");

            // Absent in one counts as 0 for that test; result rounded up
            const combined = Math.ceil(((s1 || 0) + (s2 || 0)) / 4);
            totalObtained += combined;
            studentObtainedTotal += combined;
            return cell(combined);
          }

          if (h.label.toLowerCase() === "weekly test 2") return null;

          const score = parseMark(marksByHead[h.id]);

          // Always add to Max
          totalMax += Number(h.max) || 0;
          studentMaxTotal += Number(h.max) || 0;

          if (score === null) return cell("AB");

          totalObtained += score;
          studentObtainedTotal += score;
          return cell(score);
        })
        .filter(Boolean)
        .join("");

      const subjectPercentage =
        totalMax > 0 ? (totalObtained / totalMax) * 100 : 0;
      const grade = getGrade(subjectPercentage);

      subjectRowsHtml += `
      <tr>
        <td class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3 text-uppercase">${subjectName.toUpperCase()}</p></td>
        ${headCells}
        ${cell(totalObtained)}
        ${cell(grade)}
      </tr>
      `;
    });

    const percentage =
      studentMaxTotal > 0 ? (studentObtainedTotal / studentMaxTotal) * 100 : 0;
    const overallGrade = getGrade(percentage);

    // -------- REMARKS ----------
    const groupedRemarks = halfYearlyRemarks.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});

    let remarksRowsHtml = "";
    for (const [category, items] of Object.entries(groupedRemarks)) {
      remarksRowsHtml += `
        <tr class="bg-warning">
          <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">${category.toUpperCase()}</p></th>
          <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">FIRST TERM</p></th>
        </tr>
      `;
      items.forEach((item) => {
        const grade = student.remarks?.["half yearly"]?.[item.id] ?? "-";
        remarksRowsHtml += `
          <tr>
            <th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">${item.label}</p></th>
            <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center text-uppercase">${grade}</p></th>
          </tr>
        `;
      });
    }

    // -------- PAGE CONTAINER ----------
    const pageContainer = document.createElement("div");
    pageContainer.style.position = "fixed";
    pageContainer.style.top = "-10000px";
    pageContainer.style.left = "-10000px";
    pageContainer.style.width = "297mm";
    pageContainer.style.height = "210mm";
    pageContainer.style.backgroundColor = "#fff";
    pageContainer.style.display = "flex";
    pageContainer.style.flexDirection = "column";
    pageContainer.style.justifyContent = "space-between";

    pageContainer.innerHTML = `
    <div class="mx-3 my-3">
      <div class="bg-watermark-logo">
        <div class="bg-primary rounded-4 px-3 py-1 mb-2">
          <div class="row align-items-center my-1">
            <div class="col-3 d-flex align-items-center">
              <img src="/assets/images/icon.png" width="70" height="70"/>
              <div class="ms-7">
                <h5 class="m-0 lh-1 text-white text-center">Academic Session</h5>
                <h3 class="text-white fw-bold lh-sm text-center">2026-27</h3>
              </div>
            </div>
            <div class="col-6">
              <h2 class="eczar-font text-center text-white lh-sm m-0">आचार्यकुलम्</h2>
              <p class="fs-4 text-white lh-sm m-0 text-center">Tata Road, Namkum, Ranchi - 834010</p>
              <h3 class="text-white fs-4 lh-sm mb-0 fw-bold text-center text-uppercase">FIRST TERM EXAMINATION REPORT CARD</h3>
            </div>
            <div class="col-3 d-flex align-items-end justify-content-end">
              <img src="${studentImage}" class="rounded-4 object-fit-cover" width="75" height="75" crossOrigin="anonymous" />
            </div>
          </div>
        </div>
        
        <div class="px-3 py-2 row">
          <div class="col-3">
            <p class="m-0 fs-5 text-dark lh-sm">Admission No : <span class="fw-bold">${
              student?.admission_no
            }</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Name : <span class="fw-bold">${studentName}</span></p>
          </div>
          <div class="col-4">
            <p class="m-0 fs-5 text-dark lh-sm">Father's Name : <span class="fw-bold">${fatherName}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Mother's Name : <span class="fw-bold">${motherName}</span></p>
          </div>
          <div class="col-2">
            <p class="m-0 fs-5 text-dark lh-sm">Class / Section : <span class="fw-bold">${currentClass} / ${section}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Roll No : <span class="fw-bold">${rollNo}</span></p>
          </div>
          <div class="col-3">
            <p class="m-0 fs-5 text-dark lh-sm">Date of Birth : <span class="fw-bold">${dob}</span></p>
            <p class="m-0 fs-5 text-dark lh-sm">Attendance : <span class="fw-bold">${totalPresentDays} / ${workingDays} (${attendancePercentage}%)</span></p>
          </div>
        </div>

        <div class="px-3 pt-2 pb-1 row">
          <div class="col-12">
            <table class="table result-table mb-2">
              <thead class="bg-warning">
                <tr>
                  <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">CO-SCHOLASTIC AREAS</p></th>
                  <th class="p-0" colspan="6"><p class="text-black m-0 fs-6 fw-normal text-center">FIRST TERM EXAMINATION &mdash; 100 MARKS</p></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">SUBJECTS</p></th>
                  ${headLabels
                    .map((h) => {
                      if (h.label.toLowerCase() === "weekly test 1") {
                        return `<th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">CYCLIC TEST (10)</p></th>`;
                      }
                      if (h.label.toLowerCase() === "weekly test 2") {
                        return null;
                      }
                      return `<th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center text-uppercase">${h.label} (${h.max})</p></th>`;
                    })
                    .filter(Boolean)
                    .join("")}
                  <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">MARKS OBTAINED (100)</p></th>
                  <th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center px-4">GRADE</p></th>
                </tr>
                ${subjectRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        
        <div class="px-3 pb-2 row g-3">
          <div class="col-4">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">GRAND TOTAL &mdash; ${studentObtainedTotal}/${studentMaxTotal}</p>
            </div>
          </div>
          <div class="col-4">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">PERCENTAGE &mdash; ${percentage.toFixed(
                2,
              )}%</p>
            </div>
          </div>
          <div class="col-4">
            <div class="bg-warning border border-1 border-dark">
              <p class="text-black m-0 fs-6 text-center">GRADE &mdash; ${overallGrade}</p>
            </div>
          </div>
        </div>

        <div class="px-3 pt-1 row g-3 pb-0 d-flex align-items-stretch">
          <div class="col-6 d-flex flex-column">
            <table class="table result-table">
              <tbody>
                ${remarksRowsHtml}
              </tbody>
            </table>
          </div>

          <div class="col-6 d-flex flex-column justify-content-between">
            <div>
              <div class="bg-warning border border-1 border-dark">
                <p class="text-black m-0 fs-6 text-center">CLASS TEACHER'S REMARKS</p>
              </div>
              <div class="border border-1 border-dark p-4 text-danger lh-sm">
                ${student?.teacherRemarks?.["annual"] || "-"}
              </div>
            </div>

            <table class="table result-table">
              <thead class="bg-warning">
                <tr>
                  <th class="p-0" colspan="2"><p class="text-black m-0 fs-6 fw-normal text-center">GRADING SCALE FOR SCHOLASTIC AREAS</p></th>
                </tr>
              </thead>
              <tbody>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">91-100</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">A1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">81-90</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">A2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">71-80</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">B1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">61-70</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">B2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">51-60</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">C1</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">41-50</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">C2</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">33-40</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">D</p></th></tr>
                <tr><th class="p-0"><p class="text-black m-0 fs-6 fw-normal ms-3">32 & Below</p></th><th class="p-0"><p class="text-black m-0 fs-6 fw-normal text-center">E</p></th></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="px-3 row mt-n2">
          <div class="col-6 d-flex justify-content-end align-items-center flex-column">
            ${classTeacherSignHtml}
            <p class="text-black m-0 fs-6 fw-bold text-center">CLASS TEACHER</p>
          </div>
          <div class="col-6 d-flex justify-content-end align-items-center flex-column">
            <img src="/assets/images/principal.png" width="65" crossOrigin="anonymous" class="mb-n1" />
            <p class="text-black m-0 fs-6 fw-bold text-center">PRINCIPAL</p>
          </div>
        </div>
      </div>
    </div>
  `;

    document.body.appendChild(pageContainer);

    const canvas = await html2canvas(pageContainer, {
      scale: 1.5,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, 0, 297, 210);

    pageContainer.remove();
  }

  const pdfBlob = pdf.output("blob");
  const sessionYear = "2026";
  const fileName = `${selectedClass}-${selectedSection}-FirstTerm-ReportCard-${sessionYear}.pdf`;

  saveAs(pdfBlob, fileName);
};

export default DownloadResult;
